import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  FiDollarSign, FiTrendingUp, FiTrendingDown, FiShoppingCart, 
  FiPackage, FiCalendar, FiRefreshCw, FiArrowUp, FiArrowDown,
  FiAlertTriangle, FiBox, FiClock, FiUsers,
  FiChevronDown, FiChevronUp, FiAlertCircle, FiCheckCircle, FiTrash2, FiGift, FiStar,
  FiChevronLeft, FiChevronRight, FiSearch, FiCreditCard
} from 'react-icons/fi';
import OrderDetailModal from './OrderManagement/components/OrderDetailModal';
import ShareModal from './OrderManagement/components/ShareModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Helper function to get axios config with auth token
const getAuthConfig = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const ReportsManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  
  // Check if user is super admin (logged in with env credentials)
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  
  // Overview data
  const [summary, setSummary] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [paymentMethodStats, setPaymentMethodStats] = useState(null);
  const [selectedHourlyPaymentMethod, setSelectedHourlyPaymentMethod] = useState('cash');
  const [hourlySales, setHourlySales] = useState(null);
  const [groupBy, setGroupBy] = useState('day');
  
  // Inventory data
  const [inventoryAlerts, setInventoryAlerts] = useState(null);
  const [inventoryForecast, setInventoryForecast] = useState(null);
  const [costTrend, setCostTrend] = useState(null);
  const [forecastDays, setForecastDays] = useState(7);
  const [wastedStats, setWastedStats] = useState(null);
  
  // Product data
  const [slowMoving, setSlowMoving] = useState(null);
  const [optionsAnalysis, setOptionsAnalysis] = useState(null);
  const [productsWithoutRecipe, setProductsWithoutRecipe] = useState(null);
  
  // All products sales data
  const [allProductsSales, setAllProductsSales] = useState(null);
  const [allProductsPage, setAllProductsPage] = useState(1);
  const [allProductsSearch, setAllProductsSearch] = useState('');
  const [allProductsCategory, setAllProductsCategory] = useState('all');
  const [allProductsSortBy, setAllProductsSortBy] = useState('revenue');
  const [allProductsSortOrder, setAllProductsSortOrder] = useState('desc');
  const [loadingAllProducts, setLoadingAllProducts] = useState(false);
  const [productCategories, setProductCategories] = useState([]);
  const [expandedProducts, setExpandedProducts] = useState({});
  const productSearchTimeoutRef = useRef(null);
  const productSearchValueRef = useRef('');
  
  // Customer stats
  const [customerStats, setCustomerStats] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topPointsCustomers, setTopPointsCustomers] = useState([]);
  
  // Payments report data
  const [paymentsData, setPaymentsData] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsLoadingMore, setPaymentsLoadingMore] = useState(false);
  const [paymentsFilter, setPaymentsFilter] = useState('all'); // all, cash, transfer, debt, bank_xxx
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [paymentsHasMore, setPaymentsHasMore] = useState(true);
  const [paymentsNextPage, setPaymentsNextPage] = useState(2); // Next page to load
  const paymentsPerPage = 30;
  const [showShareModal, setShowShareModal] = useState(false);
  const [shopSettings, setShopSettings] = useState(null);
  const [paymentsSummary, setPaymentsSummary] = useState({ count: 0, total: 0 }); // Summary for current filter
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(amount || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('W')) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 2) return `T${parts[1]}/${parts[0]}`;
    if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
    return dateStr;
  };

  // Helper to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to get date range based on period
  const getDateRange = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start, end;
    
    if (period === 'custom' && customDateRange.start && customDateRange.end) {
      return { start: customDateRange.start, end: customDateRange.end };
    }
    
    switch (period) {
      case 'today':
        start = end = formatLocalDate(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = formatLocalDate(yesterday);
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - 7);
        start = formatLocalDate(weekStart);
        end = formatLocalDate(today);
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        start = formatLocalDate(monthStart);
        end = formatLocalDate(today);
        break;
      case 'lastMonth':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0); // Day 0 = last day of previous month
        start = formatLocalDate(lastMonthStart);
        end = formatLocalDate(lastMonthEnd);
        break;
      case 'quarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        const quarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
        start = formatLocalDate(quarterStart);
        end = formatLocalDate(today);
        break;
      case 'year':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        start = formatLocalDate(yearStart);
        end = formatLocalDate(today);
        break;
      default:
        const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1);
        start = formatLocalDate(defaultStart);
        end = formatLocalDate(today);
    }
    
    return { start, end };
  }, [period, customDateRange]);

  const loadOverviewData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (period === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('startDate', customDateRange.start);
        params.append('endDate', customDateRange.end);
      }
      params.append('groupBy', groupBy);

      const config = getAuthConfig();
      const [summaryRes, revenueRes, costRes, paymentRes, hourlyRes] = await Promise.all([
        axios.get(`${API_URL}/reports/summary?${params.toString()}`, config),
        axios.get(`${API_URL}/reports/revenue-by-time?${params.toString()}`, config),
        axios.get(`${API_URL}/reports/cost-breakdown?${params.toString()}`, config),
        axios.get(`${API_URL}/reports/payment-methods?${params.toString()}`, config).catch(e => ({ data: { data: null } })),
        axios.get(`${API_URL}/reports/products/hourly-sales?${params.toString()}`, config).catch(e => ({ data: { data: null } }))
      ]);

      setSummary(summaryRes.data.data);
      setRevenueData(revenueRes.data.data);
      setCostBreakdown(costRes.data.data);
      if (paymentRes.data.data) setPaymentMethodStats(paymentRes.data.data);
      if (hourlyRes.data.data) setHourlySales(hourlyRes.data.data);
    } catch (error) {
      console.error('Error loading overview data:', error);
      toast.error('Không thể tải dữ liệu tổng quan');
    }
  }, [period, customDateRange, groupBy]);

  const loadInventoryData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (period === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('startDate', customDateRange.start);
        params.append('endDate', customDateRange.end);
      }

      const dateRange = getDateRange();
      const config = getAuthConfig();
      const [alertsRes, forecastRes, trendRes, wastedRes] = await Promise.all([
        axios.get(`${API_URL}/reports/inventory/alerts`, config),
        axios.get(`${API_URL}/reports/inventory/forecast?days=${forecastDays}`, config),
        axios.get(`${API_URL}/reports/inventory/cost-trend?months=6`, config),
        axios.get(`${API_URL}/wasted-items/summary?startDate=${dateRange.start}&endDate=${dateRange.end}`, config)
      ]);

      setInventoryAlerts(alertsRes.data.data);
      setInventoryForecast(forecastRes.data.data);
      setCostTrend(trendRes.data.data);
      setWastedStats(wastedRes.data.data);
    } catch (error) {
      console.error('Error loading inventory data:', error);
      toast.error('Không thể tải dữ liệu kho');
    }
  }, [period, customDateRange, forecastDays, getDateRange]);

  // Load all products sales data with pagination and sorting
  const loadAllProductsSales = useCallback(async (page = 1, search = '', category = 'all', sortBy = 'revenue', sortOrder = 'desc') => {
    setLoadingAllProducts(true);
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (period === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('startDate', customDateRange.start);
        params.append('endDate', customDateRange.end);
      }
      params.append('page', page);
      params.append('limit', 15);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      if (search) {
        params.append('search', search);
      }
      if (category && category !== 'all') {
        params.append('category', category);
      }
      
      const config = getAuthConfig();
      const res = await axios.get(`${API_URL}/reports/products/all-sales?${params.toString()}`, config);
      setAllProductsSales(res.data);
      setAllProductsPage(page);
      // Don't set search here - it's controlled by input onChange to avoid race condition
      setAllProductsCategory(category);
      setAllProductsSortBy(sortBy);
      setAllProductsSortOrder(sortOrder);
    } catch (error) {
      console.error('Error loading all products sales:', error);
      toast.error('Không thể tải dữ liệu bán hàng');
    } finally {
      setLoadingAllProducts(false);
    }
  }, [period, customDateRange]);

  const loadProductData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (period === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('startDate', customDateRange.start);
        params.append('endDate', customDateRange.end);
      }

      const config = getAuthConfig();
      const [slowRes, optionsRes, allSalesRes, categoriesRes, noRecipeRes] = await Promise.all([
        axios.get(`${API_URL}/reports/products/slow-moving?${params.toString()}`, config).catch(e => ({ data: { data: null } })),
        axios.get(`${API_URL}/reports/products/options-analysis?${params.toString()}`, config).catch(e => ({ data: { data: null } })),
        axios.get(`${API_URL}/reports/products/all-sales?${params.toString()}&page=1&limit=15&sortBy=revenue&sortOrder=desc`, config).catch(e => {
          console.error('Error loading all-sales:', e);
          return { data: { success: false, data: [], pagination: null, totals: null } };
        }),
        axios.get(`${API_URL}/categories`, config).catch(e => ({ data: { data: [] } })),
        axios.get(`${API_URL}/reports/products/without-recipe?${params.toString()}`, config).catch(e => ({ data: { data: null } }))
      ]);

      setSlowMoving(slowRes.data.data);
      setOptionsAnalysis(optionsRes.data.data);
      setProductsWithoutRecipe(noRecipeRes.data?.data || null);
      
      // Set categories for filter
      const cats = categoriesRes.data.data || categoriesRes.data || [];
      setProductCategories(cats.filter(c => c.active !== false));
      
      // Set all products sales data
      if (allSalesRes.data && allSalesRes.data.success !== false) {
        setAllProductsSales(allSalesRes.data);
        setAllProductsPage(1);
        setAllProductsSearch('');
        productSearchValueRef.current = '';
        setAllProductsCategory('all');
        setAllProductsSortBy('revenue');
        setAllProductsSortOrder('desc');
      } else {
        // Set empty state to prevent showing old data
        setAllProductsSales({ success: true, data: [], pagination: null, totals: null });
      }
    } catch (error) {
      console.error('Error loading product data:', error);
      toast.error('Không thể tải dữ liệu sản phẩm');
    }
  }, [period, customDateRange]);

  // Load customer stats
  const loadCustomerData = useCallback(async () => {
    try {
      const { start, end } = getDateRange();
      
      console.log('=== CUSTOMER REPORT DEBUG ===');
      console.log('Period selected:', period);
      console.log('Date range:', start, 'to', end);
      
      // Load ALL customers (use limit=0 to get all)
      const customersRes = await axios.get(`${API_URL}/customers?limit=0`);
      const customers = customersRes.data.data || customersRes.data || [];
      console.log('Total customers:', customers.length);
      
      // Helper to get local date string
      const getLocalDateString = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      console.log('Start date:', start);
      console.log('End date:', end);
      
      // Fetch ALL paid orders (with auth token for super admin check)
      const config = getAuthConfig();
      const ordersRes = await axios.get(`${API_URL}/orders?status=paid&page=1&limit=10000`, config);
      const allPaidOrders = ordersRes.data.data || ordersRes.data || [];
      console.log('Total paid orders fetched:', allPaidOrders.length);
      
      // Filter orders by date range using local dates
      const ordersInPeriod = allPaidOrders.filter(o => {
        const orderLocalDate = getLocalDateString(o.createdAt);
        return orderLocalDate >= start && orderLocalDate <= end;
      });
      console.log('Orders in period:', ordersInPeriod.length);
      
      // Create customer ID set for quick lookup
      const customerIdSet = new Set(customers.map(c => c._id.toString()));
      
      // Group orders by customer - ONLY orders with valid customer reference in database
      const customerOrdersMap = {};
      let ordersWithCustomer = 0;
      let ordersWithoutCustomer = 0;
      
      ordersInPeriod.forEach(order => {
        // Get customer ID from order
        let customerId = null;
        if (order.customer) {
          if (typeof order.customer === 'object' && order.customer._id) {
            customerId = order.customer._id.toString();
          } else if (typeof order.customer === 'string') {
            customerId = order.customer;
          } else {
            customerId = order.customer.toString();
          }
        }
        
        // Only count if customer exists in database
        if (customerId && customerIdSet.has(customerId)) {
          ordersWithCustomer++;
          
          if (!customerOrdersMap[customerId]) {
            customerOrdersMap[customerId] = {
              customerId: customerId,
              orders: [],
              totalSpent: 0,
              orderCount: 0
            };
          }
          
          customerOrdersMap[customerId].orders.push(order);
          customerOrdersMap[customerId].totalSpent += order.totalAmount || 0;
          customerOrdersMap[customerId].orderCount += 1;
        } else {
          ordersWithoutCustomer++;
        }
      });
      
      console.log('Orders with linked customer:', ordersWithCustomer);
      console.log('Orders without linked customer:', ordersWithoutCustomer);
      console.log('Unique customers with orders:', Object.keys(customerOrdersMap).length);
      
      // Calculate totals from orders WITH customers only
      let totalOrdersInPeriod = ordersWithCustomer;
      let totalSpentInPeriod = Object.values(customerOrdersMap).reduce((sum, c) => sum + c.totalSpent, 0);
      
      console.log('Total orders in period (linked):', totalOrdersInPeriod);
      console.log('Total spent in period (linked):', totalSpentInPeriod);
      
      // Calculate stats
      const customersWithOrdersInPeriod = Object.keys(customerOrdersMap).length;
      
      // Overall customer stats (accumulated)
      const totalCustomers = customers.length;
      const totalPoints = customers.reduce((sum, c) => sum + (c.points || 0), 0);
      const totalDebt = customers.reduce((sum, c) => sum + (c.debt || 0), 0);
      const customersWithPoints = customers.filter(c => c.points > 0).length;
      const customersWithDebt = customers.filter(c => c.debt > 0).length;
      
      // Average values
      const avgOrderValue = totalOrdersInPeriod > 0 ? totalSpentInPeriod / totalOrdersInPeriod : 0;
      const avgPointsPerCustomer = totalCustomers > 0 ? totalPoints / totalCustomers : 0;
      
      setCustomerStats({
        totalCustomers,
        totalOrders: totalOrdersInPeriod,
        totalSpent: totalSpentInPeriod,
        totalPoints,
        totalDebt,
        customersWithPoints,
        customersWithDebt,
        avgOrderValue,
        avgPointsPerCustomer,
        customersWithOrdersInPeriod
      });
      
      // Build top customers list - only customers in database with orders in period
      const customerSpendingInPeriod = customers.map(c => {
        const custId = c._id.toString();
        const mapData = customerOrdersMap[custId];
        return {
          ...c,
          spentInPeriod: mapData?.totalSpent || 0,
          ordersInPeriod: mapData?.orderCount || 0
        };
      }).filter(c => c.spentInPeriod > 0);
      
      // Sort by spending
      const sortedBySpent = [...customerSpendingInPeriod].sort((a, b) => b.spentInPeriod - a.spentInPeriod);
      
      console.log('Sorted top spenders (after sort):', sortedBySpent.slice(0, 10).map(c => ({ 
        name: c.name, 
        spent: c.spentInPeriod,
        orders: c.ordersInPeriod
      })));
      console.log('=== END DEBUG ===');
      
      setTopCustomers(sortedBySpent.slice(0, 10).map(c => ({
        ...c,
        totalSpent: c.spentInPeriod,
        totalOrders: c.ordersInPeriod
      })));
      
      // Top customers by points EARNED in period (not accumulated)
      // Calculate points earned from orders in period
      const customerPointsInPeriod = customers.map(c => {
        const custId = c._id.toString();
        const mapData = customerOrdersMap[custId];
        
        // Sum pointsEarned from orders in period
        let pointsInPeriod = 0;
        if (mapData && mapData.orders) {
          pointsInPeriod = mapData.orders.reduce((sum, o) => sum + (o.pointsEarned || 0), 0);
        }
        
        return {
          ...c,
          pointsInPeriod: pointsInPeriod,
          spentInPeriod: mapData?.totalSpent || 0,
          ordersInPeriod: mapData?.orderCount || 0
        };
      }).filter(c => c.pointsInPeriod > 0);
      
      const sortedByPoints = [...customerPointsInPeriod].sort((a, b) => b.pointsInPeriod - a.pointsInPeriod);
      setTopPointsCustomers(sortedByPoints.slice(0, 10).map(c => ({
        ...c,
        points: c.pointsInPeriod // Override points with period points for display
      })));
      
    } catch (error) {
      console.error('Error loading customer data:', error);
      toast.error('Không thể tải dữ liệu khách hàng');
    }
  }, [getDateRange, period]);

  // Load payments data for payments report
  const loadPaymentsData = useCallback(async (pageToLoad = 1, isReset = true, filterValue = 'all') => {
    // Prevent duplicate loading - check if already loading
    if (isReset) {
      setPaymentsLoading(true);
      setPaymentsData([]);
      setPaymentsHasMore(true);
      setPaymentsNextPage(2);
      setPaymentsSummary({ count: 0, total: 0 });
    } else {
      // For non-reset (loading more), set loading state
      setPaymentsLoadingMore(true);
    }
    
    try {
      const { start, end } = getDateRange();
      
      // Load bank accounts from shop settings (if not already loaded)
      let currentBankAccounts = bankAccounts;
      if (bankAccounts.length === 0) {
        try {
          const settingsRes = await axios.get(`${API_URL}/shop-settings`);
          // API returns { success: true, data: settings } or just settings
          const settings = settingsRes.data?.data || settingsRes.data;
          setShopSettings(settings);
          if (settings?.bankAccounts && settings.bankAccounts.length > 0) {
            setBankAccounts(settings.bankAccounts);
            currentBankAccounts = settings.bankAccounts;
          }
        } catch (err) {
          console.error('Error loading bank accounts:', err);
        }
      }
      
      // Load summary for this filter (only on reset/filter change)
      if (isReset) {
        try {
          let summaryParams = `startDate=${start}&endDate=${end}`;
          
          if (filterValue === 'cash') {
            summaryParams += `&filter=cash`;
          } else if (filterValue === 'transfer') {
            summaryParams += `&filter=transfer`;
          } else if (filterValue === 'debt') {
            summaryParams += `&filter=debt`;
          } else if (filterValue.startsWith('bank_')) {
            const bankIndex = parseInt(filterValue.split('_')[1]);
            const bank = currentBankAccounts[bankIndex];
            if (bank) {
              summaryParams += `&bankAccountNumber=${encodeURIComponent(bank.accountNumber)}`;
            }
          }
          
          const config = getAuthConfig();
          const summaryRes = await axios.get(`${API_URL}/reports/payment-summary?${summaryParams}`, config);
          if (summaryRes.data.success) {
            setPaymentsSummary(summaryRes.data.data);
          }
        } catch (err) {
          console.error('Error loading payment summary:', err);
        }
      }
      
      // Build query params - use skipCount for faster queries (no COUNT)
      let queryParams = `status=paid&page=${pageToLoad}&limit=${paymentsPerPage}&skipCount=true`;
      
      // Add date filter
      if (start && end) {
        queryParams += `&startDate=${start}&endDate=${end}`;
      }
      
      // Add payment method filter for backend
      if (filterValue === 'cash') {
        queryParams += `&paymentMethod=cash`;
      } else if (filterValue === 'debt') {
        queryParams += `&paymentMethod=hasDebt`;
      } else if (filterValue === 'transfer' || filterValue.startsWith('bank_')) {
        // For transfer and bank_X filters, load transfer payments
        // bank_X will be further filtered client-side by specific bank account
        queryParams += `&paymentMethod=transfer`;
        
        // Add bank account filter to backend query
        if (filterValue.startsWith('bank_')) {
          const bankIndex = parseInt(filterValue.split('_')[1]);
          const bank = currentBankAccounts[bankIndex];
          if (bank) {
            queryParams += `&bankAccountNumber=${encodeURIComponent(bank.accountNumber)}`;
          }
        }
      }
      
      // Fetch orders with pagination (with auth token for super admin check)
      const config = getAuthConfig();
      const ordersRes = await axios.get(`${API_URL}/orders?${queryParams}`, config);
      const ordersData = ordersRes.data;
      const orders = ordersData.data || ordersData || [];
      // Backend returns { success, data, pagination: { hasMore } } when skipCount=true
      const hasMore = ordersData.pagination?.hasMore || false;
      
      // Build payments list from this batch
      const newPayments = [];
      
      orders.forEach(order => {
        const orderCode = order.orderCode || (order.createdAt ? 
          `${new Date(order.createdAt).getDate().toString().padStart(2,'0')}${(new Date(order.createdAt).getMonth()+1).toString().padStart(2,'0')}${order._id.slice(-4).toUpperCase()}` : 
          order._id.slice(-6).toUpperCase());
        
        // Direct payment (at checkout time)
        if (order.paymentMethod) {
          const paymentAmount = order.totalAmount - (order.debtAmount || 0) - (order.pointsDiscount || 0);
          
          if (paymentAmount > 0) {
            newPayments.push({
              _id: order._id,
              orderId: order._id,
              orderCode: orderCode,
              date: order.createdAt,
              customerName: order.customer?.name || order.customerName || 'Khách vãng lai',
              customerId: order.customer?._id || order.customer,
              amount: paymentAmount,
              paymentMethod: order.paymentMethod,
              bankAccount: order.bankAccount,
              paymentType: 'direct',
              order: order
            });
          }
        }
        
        // Debt payment (separate from checkout)
        if (order.debtAmount > 0 && order.debtPaidAt) {
          newPayments.push({
            _id: `${order._id}_debt`,
            orderId: order._id,
            orderCode: orderCode,
            date: order.debtPaidAt,
            customerName: order.customer?.name || order.customerName || 'Khách vãng lai',
            customerId: order.customer?._id || order.customer,
            amount: order.debtAmount,
            paymentMethod: order.debtPaidMethod || 'cash',
            bankAccount: order.debtPaidBankAccount,
            paymentType: 'debt',
            order: order
          });
        }
        
        // Pending debt (not yet paid)
        if (order.debtAmount > 0 && !order.debtPaidAt) {
          newPayments.push({
            _id: `${order._id}_pending`,
            orderId: order._id,
            orderCode: orderCode,
            date: order.createdAt,
            customerName: order.customer?.name || order.customerName || 'Khách vãng lai',
            customerId: order.customer?._id || order.customer,
            amount: order.debtAmount,
            paymentMethod: 'pending_debt',
            bankAccount: null,
            paymentType: 'pending',
            order: order
          });
        }
      });
      
      if (isReset) {
        setPaymentsData(newPayments);
        setPaymentsNextPage(2);
      } else {
        setPaymentsData(prev => [...prev, ...newPayments]);
        setPaymentsNextPage(pageToLoad + 1);
      }
      
      // Use hasMore directly from backend response
      setPaymentsHasMore(hasMore);
      
    } catch (error) {
      console.error('Error loading payments data:', error);
      toast.error('Không thể tải dữ liệu thanh toán');
    } finally {
      setPaymentsLoading(false);
      setPaymentsLoadingMore(false);
    }
  }, [getDateRange, bankAccounts.length]);

  // Load more payments - called from scroll
  const loadMorePayments = useCallback(() => {
    if (!paymentsLoadingMore && paymentsHasMore && !paymentsLoading) {
      loadPaymentsData(paymentsNextPage, false, paymentsFilter);
    }
  }, [paymentsLoadingMore, paymentsHasMore, paymentsLoading, paymentsNextPage, paymentsFilter, loadPaymentsData]);

  // Handle filter change - reload from backend
  const handlePaymentsFilterChange = useCallback((newFilter) => {
    setPaymentsFilter(newFilter);
    setPaymentsNextPage(2);
    setPaymentsHasMore(true);
    loadPaymentsData(1, true, newFilter);
  }, [loadPaymentsData]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        await loadOverviewData();
      } else if (activeTab === 'payments') {
        // Reset filter to 'all' when tab loads
        setPaymentsFilter('all');
        setPaymentsNextPage(2);
        setPaymentsHasMore(true);
        await loadPaymentsData(1, true, 'all');
      } else if (activeTab === 'inventory') {
        await loadInventoryData();
      } else if (activeTab === 'products') {
        await loadProductData();
      } else if (activeTab === 'customers') {
        await loadCustomerData();
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loadOverviewData, loadPaymentsData, loadInventoryData, loadProductData, loadCustomerData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (productSearchTimeoutRef.current) {
        clearTimeout(productSearchTimeoutRef.current);
      }
    };
  }, []);

  const periodOptions = [
    { value: 'today', label: 'Hôm nay' },
    { value: 'yesterday', label: 'Hôm qua' },
    { value: 'week', label: 'Tuần này' },
    { value: 'month', label: 'Tháng này' },
    { value: 'lastMonth', label: 'Tháng trước' },
    { value: 'quarter', label: 'Quý này' },
    { value: 'year', label: 'Năm nay' },
    { value: 'custom', label: 'Tùy chỉnh' }
  ];

  // Define tabs - hide "Thanh toán" tab for regular admin
  const allTabs = [
    { id: 'overview', label: 'Tổng quan', icon: FiTrendingUp },
    { id: 'products', label: 'Sản phẩm', icon: FiPackage },
    { id: 'payments', label: 'Thanh toán', icon: FiCreditCard, superAdminOnly: true },
    { id: 'customers', label: 'Khách hàng', icon: FiUsers },
    { id: 'inventory', label: 'Kho', icon: FiBox }
  ];
  
  // Filter tabs based on user role
  const tabs = allTabs.filter(tab => !tab.superAdminOnly || isSuperAdmin);

  const SummaryCard = ({ title, value, subValue, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-xl shadow-sm border p-3 md:p-4">
      <div className="flex items-center justify-between mb-1 md:mb-2">
        <span className="text-gray-500 text-xs md:text-sm">{title}</span>
        <div className={`p-1.5 md:p-2 rounded-lg ${color}`}>
          <Icon size={14} className="text-white md:hidden" />
          <Icon size={18} className="text-white hidden md:block" />
        </div>
      </div>
      <div className="text-base md:text-xl lg:text-2xl font-bold mb-0.5 md:mb-1">{value}</div>
      {subValue && <div className="text-[10px] md:text-xs text-gray-400">{subValue}</div>}
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-[10px] md:text-xs mt-1 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
  );

  const AlertBadge = ({ status }) => {
    const styles = {
      out: 'bg-red-100 text-red-700',
      critical: 'bg-orange-100 text-orange-700',
      low: 'bg-yellow-100 text-yellow-700'
    };
    const labels = { out: 'Hết hàng', critical: 'Nguy hiểm', low: 'Thấp' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-medium mb-2">{formatDate(label)}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}đ
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // ============================================
  // RENDER: OVERVIEW TAB
  // ============================================
  const renderOverviewTab = () => (
    <div className="space-y-4 md:space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-3 lg:gap-3">
          <SummaryCard
            title="Doanh thu"
            value={`${formatCurrency(summary.revenue.total)}đ`}
            subValue={`${summary.revenue.orderCount} đơn hàng`}
            icon={FiDollarSign}
            color="bg-green-500"
          />
          <SummaryCard
            title="CP nguyên liệu"
            value={`${formatCurrency(summary.costs.ingredient)}đ`}
            subValue="Từ công thức"
            icon={FiPackage}
            color="bg-orange-500"
          />
          <SummaryCard
            title="CP nhập hàng"
            value={`${formatCurrency(summary.costs.import)}đ`}
            subValue="Phiếu nhập"
            icon={FiShoppingCart}
            color="bg-blue-500"
          />
          <SummaryCard
            title="Chi phí khác"
            value={`${formatCurrency(summary.costs.other)}đ`}
            subValue="Khoản thanh toán"
            icon={FiCalendar}
            color="bg-purple-500"
          />
          <SummaryCard
            title="Hàng hủy"
            value={`${formatCurrency(summary.costs.wasted || 0)}đ`}
            subValue="Thiệt hại"
            icon={FiTrash2}
            color="bg-red-500"
          />
          <SummaryCard
            title="LN gộp"
            value={`${formatCurrency(summary.profit.gross)}đ`}
            subValue={`${summary.profit.grossMargin.toFixed(1)}% biên LN`}
            icon={summary.profit.gross >= 0 ? FiTrendingUp : FiTrendingDown}
            color={summary.profit.gross >= 0 ? 'bg-emerald-500' : 'bg-red-500'}
          />
          <SummaryCard
            title="LN ròng"
            value={`${formatCurrency(summary.profit.net)}đ`}
            subValue={`${summary.profit.netMargin.toFixed(1)}% sau CP`}
            icon={summary.profit.net >= 0 ? FiTrendingUp : FiTrendingDown}
            color={summary.profit.net >= 0 ? 'bg-teal-500' : 'bg-red-500'}
          />
        </div>
      )}

      {/* Row 1: Revenue vs Cost Chart (7) + Cost Breakdown (5) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Revenue vs Cost Chart - 7/12 */}
        <div className="lg:col-span-7 bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Doanh thu & Chi phí</h3>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value="day">Theo ngày</option>
              <option value="week">Theo tuần</option>
              <option value="month">Theo tháng</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="revenue" name="Doanh thu" stroke="#10B981" fill="#10B98133" strokeWidth={2} />
              <Area type="monotone" dataKey="totalCost" name="Chi phí" stroke="#EF4444" fill="#EF444433" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Breakdown Pie Chart - 5/12 */}
        {costBreakdown && (
          <div className="lg:col-span-5 bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold mb-4">Cơ cấu chi phí</h3>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <ResponsiveContainer width={isMobile ? '100%' : '55%'} height={250}>
                <PieChart>
                  <Pie
                    data={costBreakdown.breakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {costBreakdown.breakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${formatCurrency(value)}đ`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {costBreakdown.breakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span>{item.name}</span>
                    </div>
                    <span className="font-medium">{formatCurrency(item.value)}đ</span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2">
                  <div className="flex items-center justify-between font-semibold">
                    <span>Tổng chi phí</span>
                    <span>{formatCurrency(costBreakdown.totals.total)}đ</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Row 2: Payment Method Statistics - Tỷ lệ (4) + Daily Chart (8) */}
      {paymentMethodStats && paymentMethodStats.dailyData && paymentMethodStats.summary && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          {/* Pie Chart Summary - 4/12 (on left) */}
          <div className="lg:col-span-4 bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold mb-4">Tỷ lệ phương thức thanh toán</h3>
            <div className="flex flex-col items-center gap-4">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={paymentMethodStats.summary}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="totalRevenue"
                    label={({ percentage }) => `${percentage}%`}
                  >
                    {paymentMethodStats.summary.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${formatCurrency(value)}đ`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-full space-y-2">
                {paymentMethodStats.summary.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span>{item.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">{formatCurrency(item.totalRevenue)}đ</span>
                      <span className="text-xs text-gray-500 ml-1">({item.percentage}%)</span>
                    </div>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2">
                  <div className="flex items-center justify-between font-semibold text-sm">
                    <span>Tổng</span>
                    <span>{formatCurrency(paymentMethodStats.totals.totalRevenue)}đ</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Bar Chart - 8/12 */}
          <div className="lg:col-span-8 bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold mb-4">Doanh thu theo phương thức thanh toán</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={paymentMethodStats.dailyData || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate} 
                  tick={{ fontSize: 11 }} 
                  interval="preserveStartEnd" 
                />
                <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} />
                <Tooltip 
                  labelFormatter={(label) => `Ngày ${formatDate(label)}`}
                  formatter={(value, name) => {
                    const names = { cash: 'Tiền mặt', transfer: 'Chuyển khoản', debt: 'Ghi nợ' };
                    return [`${formatCurrency(value)}đ`, names[name] || name];
                  }}
                />
                <Legend 
                  formatter={(value) => {
                    const names = { cash: 'Tiền mặt', transfer: 'Chuyển khoản', debt: 'Ghi nợ' };
                    return names[value] || value;
                  }}
                />
                <Bar dataKey="cash" stackId="a" fill="#10B981" name="cash" />
                <Bar dataKey="transfer" stackId="a" fill="#3B82F6" name="transfer" />
                <Bar dataKey="debt" stackId="a" fill="#F59E0B" name="debt" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Row 3: Profit Over Time + Hourly Sales + Peak Hours */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Profit Chart */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold mb-4">Lợi nhuận theo thời gian</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="profit" name="Lợi nhuận" radius={[4, 4, 0, 0]}>
                {revenueData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.profit >= 0 ? '#10B981' : '#EF4444'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Hourly Sales Chart - Super Admin Only */}
        {isSuperAdmin && hourlySales && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FiClock className="text-blue-500" />
              Doanh thu theo giờ
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={hourlySales.hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hourLabel" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => `${formatCurrency(value)}đ`} />
                <Bar dataKey="revenue" name="Doanh thu" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Peak Hours by Payment Method - Super Admin Only */}
        {isSuperAdmin && paymentMethodStats && paymentMethodStats.hourlyByMethod && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Giờ cao điểm</h3>
              <select
                value={selectedHourlyPaymentMethod}
                onChange={(e) => setSelectedHourlyPaymentMethod(e.target.value)}
                className="text-sm border rounded-lg px-2 py-1"
              >
                <option value="cash">Tiền mặt</option>
                <option value="transfer">Chuyển khoản</option>
                <option value="debt">Ghi nợ</option>
              </select>
            </div>
            <div className="space-y-2">
              {paymentMethodStats.hourlyByMethod[selectedHourlyPaymentMethod]?.length > 0 ? (
                paymentMethodStats.hourlyByMethod[selectedHourlyPaymentMethod].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{item.hourLabel}</span>
                        <span className="text-xs text-gray-500">{item.count} đơn</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div 
                          className="h-1.5 rounded-full"
                          style={{ 
                            width: `${(item.count / (paymentMethodStats.hourlyByMethod[selectedHourlyPaymentMethod][0]?.count || 1)) * 100}%`,
                            backgroundColor: selectedHourlyPaymentMethod === 'cash' ? '#10B981' : selectedHourlyPaymentMethod === 'transfer' ? '#3B82F6' : '#F59E0B'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 text-sm py-4">
                  Không có dữ liệu
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // RENDER: INVENTORY TAB
  // ============================================
  const renderInventoryTab = () => (
    <div className="space-y-6">
      {/* Alerts Summary */}
      {inventoryAlerts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FiAlertCircle className="text-red-500" />
              <span className="text-red-700 font-medium">Hết hàng</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{inventoryAlerts.summary.outOfStock}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FiAlertTriangle className="text-orange-500" />
              <span className="text-orange-700 font-medium">Nguy hiểm</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">{inventoryAlerts.summary.critical}</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FiBox className="text-yellow-500" />
              <span className="text-yellow-700 font-medium">Tồn kho thấp</span>
            </div>
            <div className="text-2xl font-bold text-yellow-600">{inventoryAlerts.summary.low}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FiCheckCircle className="text-green-500" />
              <span className="text-green-700 font-medium">Tổng cảnh báo</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{inventoryAlerts.summary.total}</div>
          </div>
        </div>
      )}

      {/* Alerts List */}
      {inventoryAlerts && inventoryAlerts.alerts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FiAlertTriangle className="text-orange-500" />
            Cảnh báo tồn kho
          </h3>
          <div className="space-y-3">
            {inventoryAlerts.alerts.map((alert, index) => {
              const unitLabels = { bag: 'túi', box: 'hộp', can: 'lon', bottle: 'chai', piece: 'quả', kg: 'kg' };
              const unitLabel = unitLabels[alert.unit] || alert.unit;
              return (
              <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                alert.status === 'out' ? 'bg-red-50' : 
                alert.status === 'critical' ? 'bg-orange-50' : 'bg-yellow-50'
              }`}>
                <div className="flex items-center gap-3">
                  <AlertBadge status={alert.status} />
                  <div>
                    <p className="font-medium">{alert.name}</p>
                    <p className="text-sm text-gray-500">{alert.message}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold">{alert.currentStock} {unitLabel}</p>
                  <p className="text-xs text-gray-500">Min: {alert.minStock}</p>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cost Trend - moved here */}
      {costTrend && costTrend.items.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FiTrendingUp className="text-purple-500" />
            Xu hướng giá nhập (6 tháng)
          </h3>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{costTrend.summary.increasing}</div>
              <div className="text-sm text-red-700">Tăng giá</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">{costTrend.summary.stable}</div>
              <div className="text-sm text-gray-700">Ổn định</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{costTrend.summary.decreasing}</div>
              <div className="text-sm text-green-700">Giảm giá</div>
            </div>
          </div>

          <div className="space-y-2">
            {costTrend.items.filter(i => i.priceChange !== 0).slice(0, 10).map((item, index) => (
              <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <span>{item.name}</span>
                <span className={`font-medium flex items-center gap-1 ${
                  item.trend === 'up' ? 'text-red-600' : 
                  item.trend === 'down' ? 'text-green-600' : 'text-gray-600'
                }`}>
                  {item.trend === 'up' ? <FiChevronUp /> : item.trend === 'down' ? <FiChevronDown /> : null}
                  {item.priceChange > 0 ? '+' : ''}{item.priceChange}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Forecast */}
      {inventoryForecast && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <FiCalendar className="text-blue-500" />
              Dự báo nhu cầu
            </h3>
            <select
              value={forecastDays}
              onChange={(e) => setForecastDays(parseInt(e.target.value))}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value={7}>7 ngày tới</option>
              <option value={14}>14 ngày tới</option>
              <option value={30}>30 ngày tới</option>
            </select>
          </div>
          
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>{inventoryForecast.summary.needToOrder}</strong> mặt hàng cần đặt thêm • 
              <strong className="text-red-600 ml-2">{inventoryForecast.summary.willRunOut}</strong> sẽ hết trong {forecastDays} ngày
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Mặt hàng</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Tồn</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">TB/ngày</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Cần {forecastDays} ngày</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Còn (ngày)</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Cần đặt</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {inventoryForecast.items.filter(i => i.dailyAverage > 0).slice(0, 20).map((item, index) => {
                  const recipeUnitLabels = { g: 'g', ml: 'ml', piece: 'quả', unit: 'cái' };
                  const packagingUnitLabels = { bag: 'túi', box: 'hộp', can: 'lon', bottle: 'chai', piece: 'quả', kg: 'kg' };
                  const recipeUnitLabel = recipeUnitLabels[item.recipeUnit] || item.recipeUnit || item.unit;
                  const unitLabel = packagingUnitLabels[item.unit] || item.unit;
                  const hasConversion = item.stockInRecipeUnit && item.stockInRecipeUnit !== item.currentStock;
                  
                  return (
                  <tr key={index} className={`hover:bg-gray-50 ${item.needToOrder ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded mr-2 ${
                        item.type === 'ingredient' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.type === 'ingredient' ? 'NL' : 'VD'}
                      </span>
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>{item.currentStock} {unitLabel}</div>
                      {hasConversion && (
                        <div className="text-xs text-gray-400">= {item.stockInRecipeUnit} {recipeUnitLabel}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span>{item.dailyAverage}</span>
                      <span className="text-xs text-gray-400 ml-1">{recipeUnitLabel}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span>{item.forecastNeed}</span>
                      <span className="text-xs text-gray-400 ml-1">{recipeUnitLabel}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${
                        item.daysUntilOut === null ? 'text-green-600' :
                        item.daysUntilOut <= 3 ? 'text-red-600' :
                        item.daysUntilOut <= 7 ? 'text-orange-600' : 'text-gray-600'
                      }`}>
                        {item.daysUntilOut === null ? '∞' : item.daysUntilOut}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {item.needToOrder ? (
                        <span className="font-medium text-red-600">{item.orderAmount} {unitLabel}</span>
                      ) : '-'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Wasted Items Statistics */}
      {wastedStats && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <FiTrash2 className="text-red-500" />
            Thống kê hàng hủy
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-red-50 rounded-lg p-3">
              <span className="text-red-600 text-xs">Tổng thiệt hại</span>
              <div className="text-xl font-bold text-red-700">{formatCurrency(wastedStats.total?.totalLoss || 0)}đ</div>
              <span className="text-xs text-gray-500">{wastedStats.total?.count || 0} lần hủy</span>
            </div>
            
            {(wastedStats.byReason || []).slice(0, 3).map((item, index) => {
              const reasonLabels = {
                expired: 'Hết hạn',
                damaged: 'Hư hỏng',
                end_of_day: 'Cuối ngày',
                quality: 'Chất lượng',
                other: 'Khác'
              };
              const reasonColors = {
                expired: 'bg-orange-50 text-orange-700',
                damaged: 'bg-red-50 text-red-700',
                end_of_day: 'bg-purple-50 text-purple-700',
                quality: 'bg-yellow-50 text-yellow-700',
                other: 'bg-gray-50 text-gray-700'
              };
              return (
                <div key={index} className={`${reasonColors[item._id]?.split(' ')[0] || 'bg-gray-50'} rounded-lg p-3`}>
                  <span className={`text-xs ${reasonColors[item._id]?.split(' ')[1] || 'text-gray-600'}`}>
                    {reasonLabels[item._id] || item._id}
                  </span>
                  <div className={`text-lg font-bold ${reasonColors[item._id]?.split(' ')[1] || 'text-gray-700'}`}>
                    {formatCurrency(item.totalLoss)}đ
                  </div>
                  <span className="text-xs text-gray-500">{item.count} lần</span>
                </div>
              );
            })}
          </div>

          {/* By Type */}
          <div className="grid grid-cols-2 gap-4">
            {(wastedStats.byType || []).map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    item._id === 'ingredient' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {item._id === 'ingredient' ? 'Nguyên liệu' : 'Vật dụng'}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-red-600">{formatCurrency(item.totalLoss)}đ</div>
                  <div className="text-xs text-gray-500">{item.count} lần hủy</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // RENDER: PRODUCTS TAB
  // ============================================
  const handleProductsSort = (column) => {
    const newOrder = allProductsSortBy === column && allProductsSortOrder === 'desc' ? 'asc' : 'desc';
    loadAllProductsSales(1, allProductsSearch, allProductsCategory, column, newOrder);
  };

  const SortHeader = ({ column, label }) => (
    <th 
      className="text-right py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
      onClick={() => handleProductsSort(column)}
    >
      <div className="flex items-center justify-end gap-1">
        <span>{label}</span>
        {allProductsSortBy === column && (
          allProductsSortOrder === 'desc' ? <FiChevronDown size={14} /> : <FiChevronUp size={14} />
        )}
      </div>
    </th>
  );

  const renderProductsTab = () => (
    <div className="space-y-6">
      {/* Products Without Recipe Alert */}
      {productsWithoutRecipe && productsWithoutRecipe.products && productsWithoutRecipe.products.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FiAlertTriangle className="text-orange-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-orange-800 mb-1 flex items-center gap-2">
                Sản phẩm bán được nhưng chưa có công thức
                <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded-full">
                  {productsWithoutRecipe.totals?.count || productsWithoutRecipe.products.length}
                </span>
              </h3>
              <p className="text-sm text-orange-700 mb-3">
                Các sản phẩm sau đã có doanh số bán hàng nhưng chưa được thiết lập công thức, không thể tính giá vốn và tự động trừ kho.
              </p>
              
              {/* Summary Stats */}
              <div className="flex flex-wrap gap-4 mb-3 text-sm">
                <div className="flex items-center gap-1">
                  <FiPackage className="text-orange-600" size={14} />
                  <span className="text-orange-800">
                    <strong>{productsWithoutRecipe.totals?.totalQuantity || 0}</strong> sản phẩm đã bán
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <FiDollarSign className="text-orange-600" size={14} />
                  <span className="text-orange-800">
                    <strong>{formatCurrency(productsWithoutRecipe.totals?.totalRevenue || 0)}đ</strong> doanh thu
                  </span>
                </div>
              </div>
              
              {/* Products List */}
              <div className="bg-white rounded-lg border border-orange-200 overflow-hidden">
                <div className="max-h-[250px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-orange-100 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium text-orange-800">Sản phẩm</th>
                        <th className="text-right p-2 font-medium text-orange-800 hidden sm:table-cell">Danh mục</th>
                        <th className="text-right p-2 font-medium text-orange-800">SL bán</th>
                        <th className="text-right p-2 font-medium text-orange-800">Doanh thu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productsWithoutRecipe.products.map((product, index) => (
                        <tr key={`${product.menuItemId}-${product.optionName || 'default'}-${index}`} className="border-t border-orange-100 hover:bg-orange-50">
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              {product.image ? (
                                <img src={product.image} alt="" className="w-8 h-8 rounded object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded bg-orange-100 flex items-center justify-center">
                                  <FiPackage size={14} className="text-orange-400" />
                                </div>
                              )}
                              <div>
                                <div className="font-medium text-gray-800">{product.displayName}</div>
                                {product.hasAnyRecipe && (
                                  <div className="text-xs text-orange-600">Thiếu công thức cho size này</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-2 text-right text-gray-500 hidden sm:table-cell">{product.category}</td>
                          <td className="p-2 text-right font-medium text-orange-700">{product.quantity}</td>
                          <td className="p-2 text-right font-medium text-orange-700">{formatCurrency(product.revenue)}đ</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <p className="text-xs text-orange-600 mt-2">
                💡 Vào <strong>Menu → Công thức</strong> để tạo công thức cho các sản phẩm này.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* All Products Sales Stats */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <FiShoppingCart className="text-green-500" />
            Thống kê bán hàng theo sản phẩm
          </h3>
          
          {/* Search and Category Filter */}
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Category Filter */}
            <select
              value={allProductsCategory}
              onChange={(e) => {
                const newCategory = e.target.value;
                setAllProductsCategory(newCategory);
                loadAllProductsSales(1, allProductsSearch, newCategory, allProductsSortBy, allProductsSortOrder);
              }}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value="all">Tất cả danh mục</option>
              {productCategories.map(cat => (
                <option key={cat._id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={allProductsSearch}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setAllProductsSearch(newValue);
                  productSearchValueRef.current = newValue;
                  // Debounce search using ref
                  if (productSearchTimeoutRef.current) {
                    clearTimeout(productSearchTimeoutRef.current);
                  }
                  productSearchTimeoutRef.current = setTimeout(() => {
                    // Use ref value to get latest search term
                    loadAllProductsSales(1, productSearchValueRef.current, allProductsCategory, allProductsSortBy, allProductsSortOrder);
                  }, 300);
                }}
                placeholder="Tìm sản phẩm..."
                className="pl-9 pr-3 py-2 border rounded-lg text-sm w-full sm:w-[200px]"
              />
            </div>
          </div>
        </div>
        
        {/* Totals Summary */}
        {allProductsSales?.totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <div className="text-lg font-bold text-green-600">{allProductsSales.totals.totalQuantity}</div>
              <div className="text-xs text-green-700">Tổng SL bán</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-lg font-bold text-blue-600">{formatCurrency(allProductsSales.totals.totalRevenue)}đ</div>
              <div className="text-xs text-blue-700">Tổng doanh thu</div>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg text-center">
              <div className="text-lg font-bold text-orange-600">{formatCurrency(allProductsSales.totals.totalCost || 0)}đ</div>
              <div className="text-xs text-orange-700">Tổng giá vốn</div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-center">
              <div className={`text-lg font-bold ${(allProductsSales.totals.totalProfit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(allProductsSales.totals.totalProfit || 0)}đ
              </div>
              <div className="text-xs text-emerald-700">Tổng lợi nhuận</div>
            </div>
          </div>
        )}
        
        {/* Products Table */}
        {loadingAllProducts ? (
          <div className="flex items-center justify-center py-8">
            <FiRefreshCw className="animate-spin text-gray-400" size={24} />
          </div>
        ) : allProductsSales && allProductsSales.data && allProductsSales.data.length > 0 ? (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3 font-medium text-gray-600 w-10">#</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Sản phẩm</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Giá bán</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Giá vốn</th>
                    <SortHeader column="quantity" label="SL bán" />
                    <th className="text-right py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Tổng giá vốn</th>
                    <SortHeader column="revenue" label="Doanh thu" />
                    <SortHeader column="profit" label="Lợi nhuận" />
                  </tr>
                </thead>
                <tbody>
                  {allProductsSales.data.map((product, index) => (
                    <React.Fragment key={product._id}>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-500">
                          {(allProductsPage - 1) * 15 + index + 1}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            {product.image ? (
                              <img src={product.image} alt="" className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
                                <FiPackage size={14} className="text-gray-400" />
                              </div>
                            )}
                            <span className="font-medium">{product.name}</span>
                            {product.optionsBreakdown && (
                              <button
                                onClick={() => setExpandedProducts(prev => ({
                                  ...prev,
                                  [product._id]: !prev[product._id]
                                }))}
                                className="text-gray-400 hover:text-blue-500 transition-colors"
                                title={expandedProducts[product._id] ? 'Thu gọn' : 'Xem chi tiết size'}
                              >
                                {expandedProducts[product._id] ? (
                                  <FiChevronUp size={16} />
                                ) : (
                                  <FiChevronDown size={16} />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right text-blue-600 font-medium">
                          {product.optionsBreakdown ? '-' : `${formatCurrency(product.price || 0)}đ`}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {product.optionsBreakdown ? '-' : `${formatCurrency(product.unitCost || 0)}đ`}
                        </td>
                        <td className={`py-2 px-3 text-right font-medium ${product.quantity > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          {product.quantity}
                        </td>
                        <td className="py-2 px-3 text-right text-orange-600">
                          {formatCurrency(product.totalCost || 0)}đ
                        </td>
                        <td className={`py-2 px-3 text-right ${product.revenue > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                          {formatCurrency(product.revenue)}đ
                        </td>
                        <td className={`py-2 px-3 text-right ${product.profit > 0 ? 'text-emerald-600' : product.profit < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          <div className="font-medium">{formatCurrency(product.profit || 0)}đ</div>
                          {product.revenue > 0 && (
                            <div className="text-xs text-gray-500">({product.margin || 0}%)</div>
                          )}
                        </td>
                      </tr>
                      {/* Options breakdown sublines - only show when expanded */}
                      {product.optionsBreakdown && expandedProducts[product._id] && product.optionsBreakdown.map((opt, optIndex) => {
                        const optMargin = opt.revenue > 0 ? Math.round((opt.profit / opt.revenue) * 100) : 0;
                        return (
                          <tr key={`${product._id}-opt-${optIndex}`} className="bg-gray-50/50 border-b text-sm">
                            <td className="py-1.5 px-3"></td>
                            <td className="py-1.5 px-3 pl-12">
                              <span className="text-gray-600">↳ {opt.name}</span>
                            </td>
                            <td className="py-1.5 px-3 text-right text-blue-600">
                              {formatCurrency((product.price || 0) + (opt.price || 0))}đ
                            </td>
                            <td className="py-1.5 px-3 text-right text-gray-500">
                              {formatCurrency(opt.unitCost || 0)}đ
                            </td>
                            <td className="py-1.5 px-3 text-right text-green-600">{opt.quantity}</td>
                            <td className="py-1.5 px-3 text-right text-orange-500">
                              {formatCurrency(opt.totalCost || 0)}đ
                            </td>
                            <td className="py-1.5 px-3 text-right text-blue-500">{formatCurrency(opt.revenue)}đ</td>
                            <td className={`py-1.5 px-3 text-right ${opt.profit > 0 ? 'text-emerald-500' : opt.profit < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                              <div>{formatCurrency(opt.profit || 0)}đ</div>
                              {opt.revenue > 0 && <div className="text-xs text-gray-400">({optMargin}%)</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Cards */}
            <div className="md:hidden space-y-2">
              {allProductsSales.data.map((product, index) => (
                <div key={product._id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 text-sm w-6">{(allProductsPage - 1) * 15 + index + 1}</span>
                    {product.image ? (
                      <img src={product.image} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                        <FiPackage size={16} className="text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{product.name}</span>
                        {product.optionsBreakdown && (
                          <button
                            onClick={() => setExpandedProducts(prev => ({
                              ...prev,
                              [product._id]: !prev[product._id]
                            }))}
                            className="text-gray-400 hover:text-blue-500"
                          >
                            {expandedProducts[product._id] ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                          </button>
                        )}
                      </div>
                      {!product.optionsBreakdown && (
                        <div className="text-xs text-gray-500">
                          Giá: <span className="text-blue-600">{formatCurrency(product.price || 0)}đ</span> • Vốn: {formatCurrency(product.unitCost || 0)}đ
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                    <div className="text-center">
                      <div className={`font-medium ${product.quantity > 0 ? 'text-green-600' : 'text-gray-400'}`}>{product.quantity}</div>
                      <div className="text-gray-500">SL</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-orange-600">{formatCurrency(product.totalCost || 0)}đ</div>
                      <div className="text-gray-500">Tổng vốn</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-medium ${product.revenue > 0 ? 'text-blue-600' : 'text-gray-400'}`}>{formatCurrency(product.revenue)}đ</div>
                      <div className="text-gray-500">DT</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-medium ${product.profit > 0 ? 'text-emerald-600' : product.profit < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {formatCurrency(product.profit || 0)}đ
                      </div>
                      <div className="text-gray-500">LN {product.revenue > 0 ? `(${product.margin || 0}%)` : ''}</div>
                    </div>
                  </div>
                  {/* Options breakdown for mobile - only show when expanded */}
                  {product.optionsBreakdown && expandedProducts[product._id] && (
                    <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                      {product.optionsBreakdown.map((opt, optIndex) => {
                        const optPrice = (product.price || 0) + (opt.price || 0);
                        const optMargin = opt.revenue > 0 ? Math.round((opt.profit / opt.revenue) * 100) : 0;
                        return (
                          <div key={optIndex} className="text-xs bg-white rounded p-2">
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-medium text-gray-700">↳ {opt.name}</span>
                              <span className="text-blue-600">{formatCurrency(optPrice)}đ</span>
                            </div>
                            <div className="flex justify-between text-gray-500">
                              <span>SL: <span className="text-green-600">{opt.quantity}</span> • Vốn: {formatCurrency(opt.unitCost || 0)}đ</span>
                              <span className={opt.profit > 0 ? 'text-emerald-600' : opt.profit < 0 ? 'text-red-600' : ''}>
                                LN: {formatCurrency(opt.profit || 0)}đ ({optMargin}%)
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {allProductsSales.pagination && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-gray-500">
                  Trang {allProductsSales.pagination.page} / {allProductsSales.pagination.totalPages}
                  <span className="text-gray-400 ml-2">({allProductsSales.pagination.totalCount} sản phẩm)</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadAllProductsSales(allProductsPage - 1, allProductsSearch, allProductsCategory, allProductsSortBy, allProductsSortOrder)}
                    disabled={!allProductsSales.pagination.hasPrev || loadingAllProducts}
                    className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-1 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} />
                    <span className="hidden sm:inline">Trang trước</span>
                  </button>
                  <button
                    onClick={() => loadAllProductsSales(allProductsPage + 1, allProductsSearch, allProductsCategory, allProductsSortBy, allProductsSortOrder)}
                    disabled={!allProductsSales.pagination.hasNext || loadingAllProducts}
                    className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-1 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="hidden sm:inline">Trang sau</span>
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {allProductsSearch ? 'Không tìm thấy sản phẩm phù hợp' : 'Không có dữ liệu'}
          </div>
        )}
      </div>

      {/* Row: Slow Moving Products + Options Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Slow Moving Products */}
        {slowMoving && slowMoving.products && (
          <div className="bg-white rounded-xl shadow-sm border p-4 min-h-[350px]">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FiTrendingDown className="text-red-500" />
              Sản phẩm bán chậm
            </h3>
            {slowMoving.products.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {slowMoving.products.slice(0, 15).map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      {product.image ? (
                        <img src={product.image} alt="" className="w-8 h-8 rounded object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center">
                          <FiPackage size={14} className="text-gray-400" />
                        </div>
                      )}
                      <span className="text-sm">{product.name}</span>
                    </div>
                    <div className="text-right">
                      <span className={`font-medium ${product.quantitySold === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                        {product.quantitySold} sp
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Tất cả sản phẩm đều bán tốt trong kỳ này</p>
            )}
          </div>
        )}

        {/* Options Analysis - Redesigned */}
        {optionsAnalysis && (
          <div className="bg-white rounded-xl shadow-sm border p-4 min-h-[350px]">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FiShoppingCart className="text-orange-500" />
              Phân tích tùy chọn khách hàng
            </h3>

            {/* Check if any data exists */}
            {(optionsAnalysis.temperature?.length > 0 || optionsAnalysis.sweetness?.length > 0 || 
              optionsAnalysis.sizeOptions?.length > 0 || optionsAnalysis.toppings?.length > 0) ? (
              <div className="space-y-4">
                {/* Temperature & Sweetness Row */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Temperature */}
                  {optionsAnalysis.temperature && optionsAnalysis.temperature.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">🧊 Đá</h4>
                      <div className="space-y-1.5">
                        {optionsAnalysis.temperature.map((t, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{t.name}</span>
                            <span className="text-sm font-medium text-blue-600">{t.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sweetness */}
                  {optionsAnalysis.sweetness && optionsAnalysis.sweetness.length > 0 && (
                    <div className="bg-pink-50 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-pink-700 mb-2 uppercase tracking-wide">🍬 Độ ngọt</h4>
                      <div className="space-y-1.5">
                        {optionsAnalysis.sweetness.map((s, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{s.name}</span>
                            <span className="text-sm font-medium text-pink-600">{s.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Size & Toppings Row */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Size Options */}
                  {optionsAnalysis.sizeOptions && optionsAnalysis.sizeOptions.length > 0 && (
                    <div className="bg-green-50 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-green-700 mb-2 uppercase tracking-wide">📏 Size</h4>
                      <div className="space-y-1.5">
                        {optionsAnalysis.sizeOptions.map((o, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{o.name}</span>
                            <span className="text-sm font-medium text-green-600">{o.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Toppings */}
                  {optionsAnalysis.toppings && optionsAnalysis.toppings.length > 0 && (
                    <div className="bg-purple-50 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-purple-700 mb-2 uppercase tracking-wide">🧋 Topping</h4>
                      <div className="space-y-1.5">
                        {optionsAnalysis.toppings.slice(0, 5).map((t, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 truncate pr-2">{t.name}</span>
                            <span className="text-sm font-medium text-purple-600">{t.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Insights */}
                {optionsAnalysis.insights && optionsAnalysis.insights.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs font-semibold text-amber-700 mb-1">💡 Gợi ý</p>
                    <ul className="text-sm text-amber-700 space-y-0.5">
                      {optionsAnalysis.insights.slice(0, 2).map((insight, i) => (
                        <li key={i}>• {insight}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Chưa có dữ liệu tùy chọn</p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // RENDER: PAYMENTS TAB
  // ============================================
  
  // Display payments - backend now supports filtering by bank account
  // Keep client-side filter as fallback for edge cases
  const getDisplayedPayments = () => {
    // Backend now filters by bankAccountNumber, so client-side filter is no longer needed
    // But keep as safety net in case of any mismatch
    if (paymentsFilter.startsWith('bank_')) {
      const bankIndex = parseInt(paymentsFilter.split('_')[1]);
      const bank = bankAccounts[bankIndex];
      if (bank) {
        // Double-check filter on client side
        return paymentsData.filter(p => 
          p.paymentMethod === 'transfer' && 
          p.bankAccount?.accountNumber === bank.accountNumber
        );
      }
    }
    return paymentsData;
  };
  
  const displayedPayments = getDisplayedPayments();
  
  const renderPaymentsTab = () => (
    <div className="space-y-4">
      {/* Filter and Summary */}
      <div className="bg-white rounded-xl shadow-sm border p-3 md:p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
            <label className="text-sm font-medium text-gray-700">Lọc theo:</label>
            <select
              value={paymentsFilter}
              onChange={(e) => handlePaymentsFilterChange(e.target.value)}
              className="flex-1 md:flex-none md:w-64 px-3 py-2 border rounded-lg text-sm"
            >
              <option value="all">Tất cả</option>
              <option value="cash">Tiền mặt</option>
              <option value="transfer">Chuyển khoản</option>
              {bankAccounts.map((bank, index) => (
                <option key={index} value={`bank_${index}`}>
                  └ {bank.bankName} - {bank.accountNumber}
                </option>
              ))}
              <option value="debt">Đang nợ</option>
            </select>
          </div>
          
          {/* Summary Stats */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Số đơn:</span>
              <span className="font-semibold text-gray-800">{paymentsSummary.count.toLocaleString('vi-VN')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Tổng tiền:</span>
              <span className="font-semibold text-green-600">{formatCurrency(paymentsSummary.total)}đ</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Payments Table with Infinite Scroll */}
      <div 
        className="bg-white rounded-xl shadow-sm border overflow-hidden"
        style={{ maxHeight: 'calc(100vh - 280px)' }}
      >
        {paymentsLoading && paymentsData.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Đang tải...</div>
        ) : displayedPayments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Không có dữ liệu</div>
        ) : (
          <>
            {/* Desktop Table */}
            <div 
              className="hidden md:block overflow-auto"
              style={{ maxHeight: 'calc(100vh - 300px)' }}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                if (scrollHeight - scrollTop - clientHeight < 200) {
                  loadMorePayments();
                }
              }}
            >
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày tháng</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mã đơn</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Khách hàng</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Số tiền</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Hình thức</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TK nhận</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Loại</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {displayedPayments.map((payment) => (
                    <tr key={payment._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(payment.date).toLocaleString('vi-VN')}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => { setSelectedOrderDetail(payment.order); setShowOrderDetailModal(true); }}
                          className="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          #{payment.orderCode}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{payment.customerName}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        <span className={payment.paymentType === 'pending' ? 'text-orange-600' : 'text-green-600'}>
                          {formatCurrency(payment.amount)}đ
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          payment.paymentMethod === 'cash' ? 'bg-green-100 text-green-700' :
                          payment.paymentMethod === 'transfer' ? 'bg-blue-100 text-blue-700' :
                          payment.paymentMethod === 'pending_debt' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {payment.paymentMethod === 'cash' ? 'Tiền mặt' :
                           payment.paymentMethod === 'transfer' ? 'Chuyển khoản' :
                           payment.paymentMethod === 'pending_debt' ? 'Chưa thanh toán' :
                           payment.paymentMethod}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {payment.bankAccount ? (
                          <span className="text-blue-600">
                            {payment.bankAccount.accountName} - {payment.bankAccount.accountNumber}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          payment.paymentType === 'direct' ? 'bg-green-100 text-green-700' :
                          payment.paymentType === 'debt' ? 'bg-blue-100 text-blue-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {payment.paymentType === 'direct' ? 'Thanh toán' :
                           payment.paymentType === 'debt' ? 'Trả nợ' :
                           'Đang nợ'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Loading more indicator */}
              {paymentsLoadingMore && (
                <div className="p-4 text-center text-gray-500">
                  <FiRefreshCw className="inline animate-spin mr-2" />
                  Đang tải thêm...
                </div>
              )}
              
              {/* Load more button */}
              {paymentsHasMore && !paymentsLoadingMore && paymentsData.length > 0 && (
                <div className="p-4 text-center">
                  <button
                    onClick={() => loadPaymentsData(paymentsNextPage, false, paymentsFilter)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Tải thêm (Trang {paymentsNextPage})
                  </button>
                </div>
              )}
              
              {/* End of data indicator */}
              {!paymentsHasMore && paymentsData.length > 0 && (
                <div className="p-4 text-center text-gray-400 text-sm">
                  Đã tải hết dữ liệu ({paymentsData.length} giao dịch)
                </div>
              )}
            </div>
            
            {/* Mobile List */}
            <div 
              className="md:hidden overflow-auto"
              style={{ maxHeight: 'calc(100vh - 300px)' }}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                if (scrollHeight - scrollTop - clientHeight < 200) {
                  loadMorePayments();
                }
              }}
            >
              <div className="divide-y divide-gray-200">
                {displayedPayments.map((payment) => (
                  <div key={payment._id} className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <button
                          onClick={() => { setSelectedOrderDetail(payment.order); setShowOrderDetailModal(true); }}
                          className="text-sm font-mono text-blue-600 hover:underline"
                        >
                          #{payment.orderCode}
                        </button>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {new Date(payment.date).toLocaleString('vi-VN')}
                        </div>
                      </div>
                      <span className={`text-sm font-bold ${payment.paymentType === 'pending' ? 'text-orange-600' : 'text-green-600'}`}>
                        {formatCurrency(payment.amount)}đ
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-900 mb-2">{payment.customerName}</div>
                    
                    <div className="flex flex-wrap gap-1">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                        payment.paymentMethod === 'cash' ? 'bg-green-100 text-green-700' :
                        payment.paymentMethod === 'transfer' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {payment.paymentMethod === 'cash' ? 'TM' :
                         payment.paymentMethod === 'transfer' ? 'CK' : 'Nợ'}
                      </span>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                        payment.paymentType === 'direct' ? 'bg-green-100 text-green-700' :
                        payment.paymentType === 'debt' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {payment.paymentType === 'direct' ? 'TT' :
                         payment.paymentType === 'debt' ? 'Trả nợ' : 'Đang nợ'}
                      </span>
                    </div>
                    
                    {payment.bankAccount && (
                      <div className="text-xs text-blue-600 mt-1">
                        {payment.bankAccount.accountName} - {payment.bankAccount.accountNumber}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Loading more indicator */}
              {paymentsLoadingMore && (
                <div className="p-4 text-center text-gray-500">
                  <FiRefreshCw className="inline animate-spin mr-2" />
                  Đang tải thêm...
                </div>
              )}
              
              {/* Load more button */}
              {paymentsHasMore && !paymentsLoadingMore && paymentsData.length > 0 && (
                <div className="p-4 text-center">
                  <button
                    onClick={() => loadPaymentsData(paymentsNextPage, false, paymentsFilter)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                  >
                    Tải thêm
                  </button>
                </div>
              )}
              
              {/* End of data indicator */}
              {!paymentsHasMore && paymentsData.length > 0 && (
                <div className="p-4 text-center text-gray-400 text-sm">
                  Đã tải hết dữ liệu ({paymentsData.length} giao dịch)
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ============================================
  // RENDER: CUSTOMERS TAB
  // ============================================
  const renderCustomersTab = () => (
    <div className="space-y-4 md:space-y-6">
      {/* Customer Stats Cards */}
      {customerStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          <div className="bg-white rounded-xl shadow-sm border p-3 md:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-500 text-xs md:text-sm">Tổng khách hàng</span>
              <div className="p-1.5 md:p-2 rounded-lg bg-green-500">
                <FiUsers size={14} className="text-white" />
              </div>
            </div>
            <div className="text-lg md:text-2xl font-bold text-green-600">{customerStats.totalCustomers}</div>
            <div className="text-[10px] md:text-xs text-gray-400">{customerStats.totalOrders} đơn hàng</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border p-3 md:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-500 text-xs md:text-sm">Tổng doanh thu</span>
              <div className="p-1.5 md:p-2 rounded-lg bg-blue-500">
                <FiDollarSign size={14} className="text-white" />
              </div>
            </div>
            <div className="text-base md:text-2xl font-bold text-blue-600">{formatCurrency(customerStats.totalSpent)}đ</div>
            <div className="text-[10px] md:text-xs text-gray-400">TB {formatCurrency(customerStats.avgOrderValue)}đ/đơn</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border p-3 md:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-500 text-xs md:text-sm">Tổng điểm thưởng</span>
              <div className="p-1.5 md:p-2 rounded-lg bg-amber-500">
                <FiGift size={14} className="text-white" />
              </div>
            </div>
            <div className="text-lg md:text-2xl font-bold text-amber-600">{new Intl.NumberFormat('vi-VN').format(customerStats.totalPoints)}</div>
            <div className="text-[10px] md:text-xs text-gray-400">{customerStats.customersWithPoints} KH có điểm</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border p-3 md:p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-500 text-xs md:text-sm">Công nợ</span>
              <div className="p-1.5 md:p-2 rounded-lg bg-orange-500">
                <FiAlertCircle size={14} className="text-white" />
              </div>
            </div>
            <div className={`text-base md:text-2xl font-bold ${customerStats.totalDebt > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
              {formatCurrency(customerStats.totalDebt)}đ
            </div>
            <div className="text-[10px] md:text-xs text-gray-400">{customerStats.customersWithDebt} KH đang nợ</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Top Customers by Spending */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FiStar className="text-yellow-500" />
            Top khách hàng chi tiêu cao
          </h3>
          {topCustomers.length > 0 ? (
            <div className="space-y-3">
              {topCustomers.map((customer, index) => (
                <div key={customer._id} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-gray-100 text-gray-700' :
                    index === 2 ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-50 text-gray-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{customer.name}</p>
                    <p className="text-xs text-gray-500">{customer.phone || 'Chưa có SĐT'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600 text-sm">{formatCurrency(customer.totalSpent || 0)}đ</p>
                    <p className="text-xs text-gray-400">{customer.totalOrders || 0} đơn</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Chưa có dữ liệu</p>
          )}
        </div>

        {/* Top Customers by Points */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FiGift className="text-amber-500" />
            Top khách hàng điểm thưởng cao
          </h3>
          {topPointsCustomers.length > 0 ? (
            <div className="space-y-3">
              {topPointsCustomers.map((customer, index) => (
                <div key={customer._id} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-amber-100 text-amber-700' :
                    index === 1 ? 'bg-gray-100 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-50 text-gray-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{customer.name}</p>
                    <p className="text-xs text-gray-500">{customer.phone || 'Chưa có SĐT'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-amber-600 text-sm flex items-center justify-end gap-1">
                      <FiGift size={12} />
                      {new Intl.NumberFormat('vi-VN').format(customer.points || 0)}
                    </p>
                    <p className="text-xs text-gray-400">{formatCurrency(customer.totalSpent || 0)}đ</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Chưa có khách hàng nào có điểm</p>
          )}
        </div>
      </div>

      {/* Points Distribution Chart */}
      {customerStats && customerStats.totalPoints > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-semibold mb-4">Phân bố điểm thưởng</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{customerStats.customersWithPoints}</p>
              <p className="text-xs text-gray-600">Có điểm</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-600">{customerStats.totalCustomers - customerStats.customersWithPoints}</p>
              <p className="text-xs text-gray-600">Chưa có điểm</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{Math.round(customerStats.avgPointsPerCustomer)}</p>
              <p className="text-xs text-gray-600">TB điểm/KH</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">
                {customerStats.totalCustomers > 0 ? Math.round(customerStats.customersWithPoints / customerStats.totalCustomers * 100) : 0}%
              </p>
              <p className="text-xs text-gray-600">Tỷ lệ tích điểm</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && !summary && !inventoryAlerts && !slowMoving) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : ""}>
      <div className={isMobile ? "px-4 py-4 space-y-4 pb-20" : "space-y-6"}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-bold flex items-center gap-2">
            <FiTrendingUp className="text-green-600" />
            Báo cáo kinh doanh
          </h1>
          <p className="text-gray-500 text-xs md:text-sm mt-0.5 md:mt-1">Phân tích doanh thu, tồn kho và sản phẩm</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-2 md:px-3 py-1.5 md:py-2 border rounded-lg text-xs md:text-sm bg-white"
          >
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          
          {period === 'custom' && (
            <div className="flex items-center gap-1 md:gap-2">
              <div 
                className="relative cursor-pointer"
                onClick={() => {
                  const dateInput = document.getElementById('report-custom-start-date');
                  if (dateInput) dateInput.showPicker();
                }}
              >
                <input
                  type="text"
                  readOnly
                  value={customDateRange.start ? (() => {
                    const [y, m, d] = customDateRange.start.split('-');
                    return `${d}/${m}/${y}`;
                  })() : ''}
                  placeholder="DD/MM/YY"
                  className="w-[90px] md:w-[120px] px-2 py-1.5 md:py-2 border rounded-lg text-xs md:text-sm cursor-pointer placeholder:text-gray-300"
                />
                <input
                  id="report-custom-start-date"
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="sr-only"
                />
              </div>
              <span className="text-gray-400 text-xs md:text-sm">→</span>
              <div 
                className="relative cursor-pointer"
                onClick={() => {
                  const dateInput = document.getElementById('report-custom-end-date');
                  if (dateInput) dateInput.showPicker();
                }}
              >
                <input
                  type="text"
                  readOnly
                  value={customDateRange.end ? (() => {
                    const [y, m, d] = customDateRange.end.split('-');
                    return `${d}/${m}/${y}`;
                  })() : ''}
                  placeholder="DD/MM/YY"
                  className="w-[90px] md:w-[120px] px-2 py-1.5 md:py-2 border rounded-lg text-xs md:text-sm cursor-pointer placeholder:text-gray-300"
                />
                <input
                  id="report-custom-end-date"
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="sr-only"
                />
              </div>
            </div>
          )}
          
          <button
            onClick={loadData}
            className="p-1.5 md:p-2 border rounded-lg hover:bg-gray-50"
            title="Làm mới"
          >
            <FiRefreshCw size={16} className={`md:hidden ${loading ? 'animate-spin' : ''}`} />
            <FiRefreshCw size={18} className={`hidden md:block ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 md:gap-2 border-b overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 text-xs md:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.id 
                ? 'border-green-600 text-green-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={14} className="md:hidden" />
            <tab.icon size={16} className="hidden md:block" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'payments' && renderPaymentsTab()}
      {activeTab === 'inventory' && renderInventoryTab()}
      {activeTab === 'products' && renderProductsTab()}
      {activeTab === 'customers' && renderCustomersTab()}
      
      {/* Order Detail Modal */}
      <OrderDetailModal
        show={showOrderDetailModal}
        order={selectedOrderDetail}
        onClose={() => { setShowOrderDetailModal(false); setSelectedOrderDetail(null); }}
        onShare={(order) => { 
          setShowShareModal(true);
          setShowOrderDetailModal(false);
        }}
        isMobile={isMobile}
        formatCurrency={formatCurrency}
      />
      
      {/* Share Modal */}
      {showShareModal && selectedOrderDetail && (
        <ShareModal
          order={selectedOrderDetail}
          shopSettings={shopSettings}
          onClose={() => setShowShareModal(false)}
        />
      )}
      </div>
    </div>
  );
};

export default ReportsManagement;
